import { FC, useState } from 'react';
import { Delete, Visibility } from '@mui/icons-material';
import { numberToCurrencyString } from 'further-ui/utils';
import { UnrealisedAssetFXGainOrLossTypeLabels } from 'further-ui/labels';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import AlertDialog from 'components/AlertDialog';
import { usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import PageSection from 'components/PageSection';
import Spacing from 'components/Spacing';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import TableActions from 'components/Table/TableActions';

const pageId = 'unrealised-asset-fx-history';

type Props = {
  vehicleId: string;
};

type RecordToDelete = {
  id: string;
};

const AssetsHistory: FC<Props> = ({ vehicleId }) => {
  const { vehicle } = useLayoutContext();
  const apiRef = useGridApiRef();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<RecordToDelete | null>(
    null,
  );

  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'createdAt',
  });

  const { data, isLoading, refetch } =
    api.unrealisedAssetFxGainOrLoss.listPosted.useQuery({
      vehicleId,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      order: pagination.order,
      orderBy: pagination.orderBy,
    });

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: deleteRecord, isPending: isDeleting } =
    api.unrealisedAssetFxGainOrLoss.delete.useMutation(
      mutationOpts({
        successMessage: 'Unrealised asset FX gain/loss deleted successfully.',
        onSuccess: () => {
          refetch();
          setDeleteDialogOpen(false);
          setRecordToDelete(null);
        },
      }),
    );

  const handleOpenDeleteDialog = (row: RecordToDelete) => {
    setRecordToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  const handleDelete = () => {
    if (recordToDelete) {
      deleteRecord({
        vehicleId,
        recordId: recordToDelete.id,
      });
    }
  };

  const columns: Array<Column> = [
    {
      field: 'ledgerDate',
      headerName: 'Ledger Date',
      renderCell: ({ row }) => row.ledgerDate.toLabel(),
      flex: 1,
    },
    {
      field: 'assetName',
      headerName: 'Asset',
      renderCell: ({ row }) => row.assetName ?? '-',
      flex: 1,
      sortable: false,
    },
    {
      field: 'holdingClassName',
      headerName: 'Holding Class',
      renderCell: ({ row }) => row.holdingClassName ?? '-',
      flex: 1,
      sortable: false,
    },
    {
      field: 'gainOrLossType',
      headerName: 'Type',
      renderCell: ({ row }) =>
        UnrealisedAssetFXGainOrLossTypeLabels[row.gainOrLossType],
      flex: 1,
      sortable: false,
    },
    {
      field: 'amountLcy',
      headerName: 'Amount (LCY)',
      renderCell: ({ row }) => {
        const isLoss =
          row.gainOrLossType === 'UnrealisedAssetFXLoss' ||
          row.gainOrLossType === 'UnrealisedAssetFXUnwindLoss';
        const amount = isLoss ? -row.amountLcy : row.amountLcy;
        return numberToCurrencyString(amount, {
          currency: vehicle?.baseCurrency,
          negativeInBrackets: true,
        });
      },
      flex: 1,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: ({ row }) => (
        <TableActions
          stopPropagation
          actions={[
            {
              label: 'View',
              icon: Visibility,
              color: 'primary',
              link: `/unrealised-asset-fx-history/${row.id}`,
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              disabled: !row.canDelete,
              tooltip: row.canDelete
                ? undefined
                : row.deleteReason ?? 'Cannot delete',
              onClick: () =>
                handleOpenDeleteDialog({
                  id: row.id!,
                }),
            },
          ]}
        />
      ),
      flex: 0.5,
      sortable: false,
    },
  ];

  if (!vehicle) {
    return null;
  }

  return (
    <Spacing>
      <PageSection
        title="Unrealised asset FX gains and losses history"
        withSpacing
      >
        <InteractiveTable
          apiRef={apiRef}
          id={pageId}
          columns={columns}
          rows={data?.results ?? []}
          pagination={pagination}
          loading={isLoading}
          scrollable
        />
        <AlertDialog
          onClose={handleCloseDeleteDialog}
          open={deleteDialogOpen}
          title="Delete unrealised asset FX gain/loss?"
          content="Are you sure you want to delete this unrealised asset FX gain/loss? This cannot be undone."
          btnLabels={{ confirm: 'Delete', cancel: 'Cancel' }}
          cancelBtnProps={{ variant: 'contained', color: 'primary' }}
          confirmBtnProps={{
            variant: 'outlined',
            color: 'error',
            disabled: isDeleting,
          }}
          onConfirm={handleDelete}
        />
      </PageSection>
    </Spacing>
  );
};

export default AssetsHistory;
